import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { AppConfigService } from '@core';
import {
  ApplyRebalanceRequestDto,
  ApplyRebalanceResponseDto,
  BulkReassignRequestDto,
  BulkReassignResponseDto,
  CapacityUpsertRequestDto,
  CapacityUpsertResponseDto,
  GenerateRebalanceProposalsRequestDto,
  GenerateRebalanceProposalsResponseDto,
  IdResponseDto,
  ManagedTeamResponseDto,
  SingleReassignRequestDto,
  SubmitRebalanceFeedbackRequestDto,
  TeamBalancingCandidateResponseDto,
  TimeOffBlockRequestDto,
  TimeOffTypeResponseDto,
  WorkloadDirectorRollupResponseDto,
  WorkloadEligibleAgentResponseDto,
  WorkloadKpiMemberPageResponseDto,
  WorkloadMemberDetailResponseDto,
  WorkloadOverviewResponseDto,
  WorkloadTeamHierarchyResponseDto,
} from '../dtos/workload-responses.dto';

@Injectable({ providedIn: 'root' })
export class TeamWorkloadApi {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(AppConfigService);
  private readonly _baseUrl = `${this._config.versionedApiUrl}/investigations/workload`;

  public getOverview(): Observable<WorkloadOverviewResponseDto> {
    return this._http.get<WorkloadOverviewResponseDto>(`${this._baseUrl}/overview`);
  }

  public getTeams(): Observable<WorkloadTeamHierarchyResponseDto[]> {
    return this._http.get<WorkloadTeamHierarchyResponseDto[]>(`${this._baseUrl}/teams`);
  }

  public getMemberDetail(
    userId: string,
    closedPage = 0,
    closedSize = 20,
  ): Observable<WorkloadMemberDetailResponseDto> {
    const params = new HttpParams()
      .set('closedPage', closedPage.toString())
      .set('closedSize', closedSize.toString());
    return this._http.get<WorkloadMemberDetailResponseDto>(
      `${this._baseUrl}/members/${userId}`,
      { params },
    );
  }

  public getKpiMembers(
    kpi: string,
    page = 0,
    size = 50,
  ): Observable<WorkloadKpiMemberPageResponseDto> {
    const params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    return this._http.get<WorkloadKpiMemberPageResponseDto>(
      `${this._baseUrl}/kpi/${kpi}/members`,
      { params },
    );
  }

  public getDirectorRollup(): Observable<WorkloadDirectorRollupResponseDto> {
    return this._http.get<WorkloadDirectorRollupResponseDto>(`${this._baseUrl}/director-rollup`);
  }

  public getTimeOffTypes(): Observable<TimeOffTypeResponseDto[]> {
    return this._http.get<TimeOffTypeResponseDto[]>(`${this._baseUrl}/time-off-types`);
  }

  public getEligibleAgents(
    investigationIds: string[],
    search?: string,
  ): Observable<WorkloadEligibleAgentResponseDto[]> {
    let params = new HttpParams().set('investigationIds', investigationIds.join(','));
    if (search) {
      params = params.set('search', search);
    }
    return this._http.get<WorkloadEligibleAgentResponseDto[]>(
      `${this._baseUrl}/eligible-agents`,
      { params },
    );
  }

  public upsertCapacity(
    userId: string,
    request: CapacityUpsertRequestDto,
  ): Observable<CapacityUpsertResponseDto> {
    return this._http.patch<CapacityUpsertResponseDto>(
      `${this._baseUrl}/members/${userId}/capacity`,
      request,
    );
  }

  public getMyManagedTeams(): Observable<ManagedTeamResponseDto[]> {
    return this._http.get<ManagedTeamResponseDto[]>(`${this._baseUrl}/me/managed-teams`);
  }

  public getMemberManagedTeams(userId: string): Observable<ManagedTeamResponseDto[]> {
    return this._http.get<ManagedTeamResponseDto[]>(
      `${this._baseUrl}/members/${userId}/managed-teams`,
    );
  }

  public addTimeOff(userId: string, request: TimeOffBlockRequestDto): Observable<IdResponseDto> {
    return this._http.post<IdResponseDto>(
      `${this._baseUrl}/members/${userId}/time-off`,
      request,
    );
  }

  public updateTimeOff(id: string, request: TimeOffBlockRequestDto): Observable<void> {
    return this._http.patch<void>(`${this._baseUrl}/time-off/${id}`, request);
  }

  public deleteTimeOff(id: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/time-off/${id}`);
  }

  public singleReassign(investigationId: string, request: SingleReassignRequestDto): Observable<void> {
    return this._http.patch<void>(`${this._baseUrl}/investigations/${investigationId}/reassign`, request);
  }

  public bulkReassign(request: BulkReassignRequestDto): Observable<BulkReassignResponseDto> {
    return this._http.post<BulkReassignResponseDto>(
      `${this._baseUrl}/investigations/bulk-reassign`,
      request,
    );
  }

  public getRebalanceCandidates(): Observable<TeamBalancingCandidateResponseDto[]> {
    return this._http.get<TeamBalancingCandidateResponseDto[]>(
      `${this._baseUrl}/rebalance/candidates`,
    );
  }

  public generateRebalanceProposals(
    request: GenerateRebalanceProposalsRequestDto,
  ): Observable<GenerateRebalanceProposalsResponseDto> {
    return this._http.post<GenerateRebalanceProposalsResponseDto>(
      `${this._baseUrl}/rebalance/proposals`,
      request,
    );
  }

  public applyRebalance(request: ApplyRebalanceRequestDto): Observable<ApplyRebalanceResponseDto> {
    return this._http.post<ApplyRebalanceResponseDto>(
      `${this._baseUrl}/rebalance/apply`,
      request,
    );
  }

  public submitRebalanceFeedback(request: SubmitRebalanceFeedbackRequestDto): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/rebalance/feedback`, request);
  }
}
